import React from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import CloseButton from '../../components/CloseButton';
import LogoutButton from '../../components/LogoutButton';
import { useAuth } from '../../contexts/AuthContext';

const TermsAndConditions = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  return (
    <>
      <Helmet>
        <title>Terms and Conditions - MyBodyToday</title>
        <meta name="description" content="MyBodyToday Terms and Conditions - User agreement and service terms" />
      </Helmet>
      
      <div className="min-h-screen bg-background px-4 py-8">
        <div className="w-full max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <Link to="/sign-in" className="inline-flex items-center text-primary hover:text-primary/80">
                <Icon name="ArrowLeft" size={20} className="mr-2" />
                Back to Sign In
              </Link>
              <div className="flex items-center gap-2">
                <CloseButton navigateTo="/sign-in" ariaLabel="Close terms and conditions" onClose={() => {}} />
                {isAuthenticated && <LogoutButton />}
              </div>
            </div>
            <h1 className="text-4xl font-bold text-foreground mb-2">
              Terms and Conditions
            </h1>
            <p className="text-muted-foreground">
              Last Updated: February 28, 2026
            </p>
          </div>

          {/* Content */}
          <div className="bg-card rounded-lg border border-border p-8 space-y-8">
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                1. Acceptance of Terms
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                By accessing or using MyBodyToday ("the Service"), you agree to be bound by these Terms and Conditions ("Terms"). If you do not agree to these Terms, you may not use the Service. These Terms constitute a legally binding agreement between you and MyBodyToday.
              </p>
            </section>

            {/* Service Description */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                2. Service Description
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                MyBodyToday is a symptom tracking and health management application that allows users to:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Record and track symptoms over time</li>
                <li>Monitor medications and treatments</li>
                <li>Generate health reports for healthcare providers</li>
                <li>Share health information with authorized caregivers</li>
                <li>Set reminders for daily check-ins and medications</li>
                <li>Analyse symptom patterns and trends</li>
              </ul>
            </section>

            {/* Medical Disclaimer */}
            <section>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Icon name="AlertTriangle" size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h2 className="text-xl font-semibold text-red-900 mb-2">
                      3. Medical Disclaimer (CRITICAL)
                    </h2>
                    <div className="text-sm text-red-800 leading-relaxed space-y-2">
                      <p>
                        <strong>NOT A MEDICAL DEVICE:</strong> MyBodyToday is NOT a medical device and is NOT intended to diagnose, treat, cure, or prevent any disease or medical condition.
                      </p>
                      <p>
                        <strong>NOT MEDICAL ADVICE:</strong> The Service does not provide medical advice, professional diagnosis, or treatment recommendations. All content is for informational purposes only.
                      </p>
                      <p>
                        <strong>CONSULT HEALTHCARE PROFESSIONALS:</strong> Always seek the advice of qualified healthcare professionals regarding any medical condition, symptom, or treatment. Never disregard professional medical advice or delay seeking it because of information from this Service.
                      </p>
                      <p>
                        <strong>EMERGENCY SITUATIONS:</strong> If you are experiencing a medical emergency, call your local emergency services (999 in the UK, 911 in the US) immediately. Do NOT rely on this Service for emergency medical assistance.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* User Accounts */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                4. User Accounts and Responsibilities
              </h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    4.1 Account Creation
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    You must provide accurate, complete, and current information during registration. You are responsible for maintaining the confidentiality of your account credentials and for all activities under your account.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    4.2 Age Requirements
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    You must be at least 13 years old to use this Service. Users under 18 should have parental or guardian consent.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    4.3 Account Security
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    You agree to notify us immediately of any unauthorized access or security breach. We recommend enabling two-factor authentication for enhanced security.
                  </p>
                </div>
              </div>
            </section>

            {/* Acceptable Use */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                5. Acceptable Use Policy
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                You agree NOT to:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Use the Service for any illegal or unauthorized purpose</li>
                <li>Violate any laws, regulations, or third-party rights</li>
                <li>Transmit viruses, malware, or harmful code</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Interfere with or disrupt the Service or servers</li>
                <li>Impersonate any person or entity</li>
                <li>Harvest or collect user information without consent</li>
                <li>Use automated systems (bots, scrapers) without permission</li>
                <li>Share your account credentials with others</li>
                <li>Use the Service to provide medical advice to others</li>
              </ul>
            </section>

            {/* Subscription and Payments */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                6. Subscription Plans and Payments
              </h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    6.1 Subscription Tiers
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    We offer multiple subscription plans (Free Trial, Basic, Premium, Ultra) with varying features and limits. Plan details and pricing are available on our Subscription Management page.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    6.2 Billing and Renewals
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Subscriptions automatically renew at the end of each billing period unless cancelled. You will be charged the then-current rate. We use Stripe for secure payment processing.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    6.3 Cancellation and Refunds
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    You may cancel your subscription at any time through your account settings. Cancellations take effect at the end of the current billing period. Refunds are provided at our discretion and in accordance with applicable consumer protection laws.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    6.4 Free Trial
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    New users may receive a free trial period. If you do not cancel before the trial ends, you will be automatically charged for the selected subscription plan.
                  </p>
                </div>
              </div>
            </section>

            {/* Intellectual Property - COMPREHENSIVE */}
            <section>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <Icon name="Shield" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-blue-900 font-medium">
                    Section 7 contains important intellectual property protections. Please read carefully.
                  </p>
                </div>
              </div>
              <h2 className="text-2xl font-semibold text-foreground mb-6">
                7. Intellectual Property Rights
              </h2>
              <div className="space-y-6">

                {/* 7.1 Copyright Ownership */}
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    7.1 Copyright Ownership
                  </h3>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    All content, features, and functionality of the Service — including but not limited to the source code, software architecture, algorithms, database schemas, user interface designs, graphics, logos, icons, text, documentation, and all other materials — are the exclusive property of MyBodyToday and are protected by copyright law, including the Copyright, Designs and Patents Act 1988 (UK) and equivalent international copyright laws.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    <strong>© 2025 MyBodyToday. All rights reserved.</strong> Unauthorised reproduction, distribution, modification, or use of any part of this Service, in whole or in part, is strictly prohibited without prior written consent from MyBodyToday.
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    This copyright protection extends to all versions, updates, and derivative works of the Service, including any improvements, enhancements, or modifications made over time.
                  </p>
                </div>

                {/* 7.2 Trademark Protection */}
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    7.2 Trademark Protection
                  </h3>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    "MyBodyToday", the MyBodyToday logo, and all related names, logos, product and service names, designs, and slogans are trademarks of MyBodyToday ("Marks"). You are not permitted to use these Marks without the prior written permission of MyBodyToday.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    You must not:
                  </p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4 mb-3">
                    <li>Use the "MyBodyToday" name, logo, or any confusingly similar mark in any business name, product name, domain name, or service</li>
                    <li>Register or attempt to register any trademark, domain name, or social media handle that incorporates "MyBodyToday" or any variation thereof</li>
                    <li>Use our Marks in any manner that implies endorsement, sponsorship, or affiliation with MyBodyToday without express written consent</li>
                    <li>Use our Marks in a way that disparages or damages the reputation of MyBodyToday</li>
                  </ul>
                  <p className="text-muted-foreground leading-relaxed">
                    Any goodwill generated through use of our Marks inures solely to the benefit of MyBodyToday.
                  </p>
                </div>

                {/* 7.3 Prohibition on Reverse Engineering and Scraping */}
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    7.3 Prohibition on Reverse Engineering, Scraping, and Copying
                  </h3>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    You expressly agree that you will NOT, directly or indirectly:
                  </p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4 mb-3">
                    <li><strong>Reverse engineer</strong> — decompile, disassemble, decode, or otherwise attempt to derive the source code, underlying ideas, algorithms, or structure of the Service</li>
                    <li><strong>Scrape or harvest</strong> — use any automated tool, bot, spider, crawler, scraper, or other means to extract, copy, or collect data, content, or information from the Service</li>
                    <li><strong>Copy or replicate</strong> — reproduce the Service's design, user interface, features, workflows, or functionality for the purpose of creating a competing product or service</li>
                    <li><strong>Circumvent protections</strong> — bypass, disable, or interfere with any security features, access controls, or technical measures protecting the Service</li>
                    <li><strong>Frame or mirror</strong> — frame, mirror, or embed any part of the Service on another website or application without written authorisation</li>
                    <li><strong>Data mine</strong> — use data mining, extraction, or similar data gathering methods on any part of the Service</li>
                    <li><strong>Create derivative works</strong> — modify, adapt, translate, or create derivative works based on the Service or its content without express written permission</li>
                  </ul>
                  <p className="text-muted-foreground leading-relaxed">
                    Any violation of this section constitutes a material breach of these Terms and may result in immediate account termination, civil legal action, and/or criminal prosecution under applicable computer misuse and intellectual property laws.
                  </p>
                </div>

                {/* 7.4 User-Generated Content Rights */}
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    7.4 User-Generated Content
                  </h3>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    You retain full ownership of the personal health data, symptom records, notes, and other content you submit to the Service ("Your Content"). By using the Service, you grant MyBodyToday a limited, non-exclusive, royalty-free, worldwide licence to:
                  </p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4 mb-3">
                    <li>Store, process, and display Your Content solely to provide the Service to you</li>
                    <li>Create anonymised, aggregated statistical data for service improvement purposes — this data will never identify you personally</li>
                    <li>Back up and restore Your Content to ensure service continuity</li>
                  </ul>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    This licence does NOT permit MyBodyToday to:
                  </p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4 mb-3">
                    <li>Sell or commercially exploit Your Content in identifiable form</li>
                    <li>Share Your Content with third parties except as described in our Privacy Policy</li>
                    <li>Use Your Content for advertising or marketing without your explicit consent</li>
                  </ul>
                  <p className="text-muted-foreground leading-relaxed">
                    You represent and warrant that Your Content does not infringe any third-party intellectual property rights and that you have all necessary rights to grant the above licence.
                  </p>
                </div>

                {/* 7.5 Prohibited Uses */}
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    7.5 Prohibited Uses of Intellectual Property
                  </h3>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    The following uses of MyBodyToday's intellectual property are strictly prohibited:
                  </p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                    <li>Building, developing, or launching a competing health tracking application that copies or substantially replicates MyBodyToday's features, design, or user experience</li>
                    <li>Using MyBodyToday's code, design patterns, or proprietary methodologies in any commercial product or service</li>
                    <li>Reproducing, republishing, or redistributing any portion of the Service's content, interface, or documentation</li>
                    <li>Using the Service to gather competitive intelligence for the purpose of developing a competing product</li>
                    <li>Licensing, sublicensing, selling, reselling, transferring, or otherwise commercially exploiting any aspect of the Service</li>
                    <li>Removing, altering, or obscuring any copyright, trademark, or other proprietary notices within the Service</li>
                    <li>Using the Service's name, branding, or reputation to promote any third-party product or service</li>
                  </ul>
                </div>

                {/* 7.6 Trade Secrets */}
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    7.6 Trade Secrets and Confidential Information
                  </h3>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    The Service contains trade secrets and confidential information belonging to MyBodyToday, including but not limited to:
                  </p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4 mb-3">
                    <li>Proprietary algorithms for symptom pattern analysis and health trend detection</li>
                    <li>Database architecture, schema design, and data processing methodologies</li>
                    <li>Business logic, pricing strategies, and operational processes</li>
                    <li>Technical infrastructure, security implementations, and system architecture</li>
                    <li>Unreleased features, product roadmaps, and development plans</li>
                  </ul>
                  <p className="text-muted-foreground leading-relaxed">
                    You agree to maintain the confidentiality of any non-public information about the Service that you may encounter and not to disclose such information to any third party.
                  </p>
                </div>

                {/* 7.7 Enforcement */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-amber-900 mb-3">
                    7.7 Enforcement and Remedies
                  </h3>
                  <p className="text-sm text-amber-800 leading-relaxed mb-3">
                    MyBodyToday takes intellectual property violations extremely seriously. Any unauthorised use of our intellectual property may result in:
                  </p>
                  <ul className="list-disc list-inside text-sm text-amber-800 space-y-2 ml-4 mb-3">
                    <li><strong>Immediate account termination</strong> without notice or refund</li>
                    <li><strong>Civil legal action</strong> for copyright infringement, trademark infringement, or misappropriation of trade secrets</li>
                    <li><strong>Injunctive relief</strong> — we may seek emergency court orders to stop infringing activity without prior notice</li>
                    <li><strong>Damages</strong> — including actual damages, statutory damages, and recovery of all legal costs and attorney fees</li>
                    <li><strong>Criminal referral</strong> where applicable under the Computer Misuse Act 1990 (UK) or equivalent legislation</li>
                  </ul>
                  <p className="text-sm text-amber-800 leading-relaxed">
                    We actively monitor for unauthorised use of our intellectual property and will pursue all available legal remedies to protect our rights. If you become aware of any infringement of our intellectual property, please report it to <strong>legal@mybodytoday.com</strong>.
                  </p>
                </div>

                {/* 7.8 Pending Trademark Status */}
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Icon name="Shield" size={20} className="text-purple-600 flex-shrink-0 mt-0.5" />
                    <h3 className="text-lg font-medium text-purple-900">
                      7.8 Pending Trademark Status
                    </h3>
                  </div>
                  <div className="space-y-3 text-sm text-purple-800 leading-relaxed">
                    <p>
                      <strong>"MyBodyToday"&#8482;</strong>, the MyBodyToday logo&#8482;, and the tagline <strong>"Track small changes before they become big ones"&#8482;</strong> are pending trademarks of MyBodyToday (the "Pending Marks"). Trademark applications have been filed and are currently under review by the relevant intellectual property authority.
                    </p>
                    <p>
                      <strong>Filing Date &amp; Priority Protection:</strong> Our trademark applications establish a priority date from the date of filing. This means that any third party who attempts to register a confusingly similar mark after our filing date will be subject to our prior rights, even before formal registration is granted. Our priority date provides legal protection from the moment of filing.
                    </p>
                    <p>
                      <strong>Unauthorised Use Prohibited:</strong> Notwithstanding the pending status of these trademark applications, any unauthorised use of the Pending Marks — including but not limited to use in business names, domain names, social media handles, product names, or marketing materials — is strictly prohibited. Such use may constitute trademark infringement and/or passing off under applicable law.
                    </p>
                    <p>
                      <strong>Use of &#8482; Symbol:</strong> The &#8482; (trademark) symbol is used to indicate that trademark rights are claimed in the Pending Marks, consistent with common law trademark rights and pending registration status. This does not imply that registration has been granted.
                    </p>
                    <p>
                      <strong>Transition to Registered Status:</strong> Upon successful registration of the Pending Marks, these Terms will be updated to reflect registered trademark status, and the ® (registered trademark) symbol will replace the &#8482; symbol. Registered status will further strengthen our enforcement rights.
                    </p>
                    <p>
                      <strong>Monitoring &amp; Enforcement:</strong> MyBodyToday actively monitors for potential infringement of the Pending Marks and will take appropriate legal action — including cease-and-desist notices, opposition proceedings, and civil litigation — against any party found to be infringing our trademark rights.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Data Privacy */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                8. Data Privacy and Security
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Your privacy is important to us. Our collection, use, and protection of your personal and health information is governed by our <Link to="/privacy-policy" className="text-primary hover:underline">Privacy Policy</Link>, which is incorporated into these Terms by reference.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                We implement industry-standard security measures, including AES-256 encryption for sensitive data. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            {/* Limitation of Liability */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                9. Limitation of Liability
              </h2>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-amber-900 leading-relaxed">
                  <strong>IMPORTANT:</strong> Please read this section carefully as it limits our liability to you.
                </p>
              </div>
              <p className="text-muted-foreground leading-relaxed mb-4">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>The Service is provided "AS IS" and "AS AVAILABLE" without warranties of any kind</li>
                <li>We do not warrant that the Service will be uninterrupted, error-free, or secure</li>
                <li>We are not liable for any health outcomes, medical decisions, or consequences resulting from use of the Service</li>
                <li>We are not responsible for data loss, though we make reasonable efforts to prevent it</li>
                <li>Our total liability to you for any claims shall not exceed the amount you paid us in the 12 months preceding the claim</li>
                <li>We are not liable for indirect, incidental, consequential, or punitive damages</li>
              </ul>
            </section>

            {/* Indemnification */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                10. Indemnification
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                You agree to indemnify, defend, and hold harmless MyBodyToday, its officers, directors, employees, and agents from any claims, liabilities, damages, losses, and expenses (including legal fees) arising from your use of the Service, violation of these Terms, or infringement of any third-party rights.
              </p>
            </section>

            {/* Termination */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                11. Termination
              </h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    11.1 By You
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    You may terminate your account at any time through your Settings &amp; Privacy page. Upon termination, your data will be deleted within 30 days.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    11.2 By Us
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    We may suspend or terminate your account if you violate these Terms, engage in fraudulent activity, or for any other reason at our discretion. We will provide notice when reasonably possible.
                  </p>
                </div>
              </div>
            </section>

            {/* Changes to Terms */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                12. Changes to Terms
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                We reserve the right to modify these Terms at any time. We will notify you of material changes via email or in-app notification. Your continued use of the Service after changes constitutes acceptance of the updated Terms.
              </p>
            </section>

            {/* Governing Law */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                13. Governing Law and Dispute Resolution
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                These Terms are governed by the laws of England and Wales. Any disputes shall be resolved through binding arbitration, except where prohibited by law. Nothing in these Terms shall prevent MyBodyToday from seeking injunctive or other equitable relief in any court of competent jurisdiction to protect its intellectual property rights.
              </p>
            </section>

            {/* Severability */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                14. Severability
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                If any provision of these Terms is found to be unenforceable or invalid, that provision will be limited or eliminated to the minimum extent necessary, and the remaining provisions will remain in full force and effect.
              </p>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                15. Contact Information
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                If you have questions about these Terms or wish to report an intellectual property violation, please contact us:
              </p>
              <div className="bg-muted rounded-lg p-4 space-y-2">
                <p className="text-foreground">
                  <strong>Legal &amp; IP Matters:</strong> legal@mybodytoday.com
                </p>
                <p className="text-foreground">
                  <strong>General Support:</strong> support@mybodytoday.com
                </p>
                <p className="text-foreground">
                  <strong>Website:</strong> mybodytoday1.com
                </p>
              </div>
            </section>

            {/* Acknowledgment */}
            <section className="border-t border-border pt-6">
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Icon name="CheckCircle" size={18} className="text-teal-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-semibold text-teal-900 mb-1">
                      Acknowledgment
                    </h3>
                    <p className="text-sm text-teal-800 leading-relaxed">
                      By creating an account and using MyBodyToday, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions and our Privacy Policy.
                    </p>
                  </div>
                </div>
              </div>
            </section>
          </div>

          {/* Footer Links */}
          <div className="mt-8 text-center space-y-2">
            <div className="space-x-4">
              <Link to="/privacy-policy" className="text-primary hover:underline">
                Privacy Policy
              </Link>
              <span className="text-muted-foreground">•</span>
              <Link to="/sign-in" className="text-primary hover:underline">
                Back to Sign In
              </Link>
            </div>
            <p className="text-sm text-muted-foreground">© 2025 MyBodyToday. All rights reserved.</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default TermsAndConditions;